import { computed } from 'vue'
import { useLicenseStore } from '@/stores/permissions/licenseStore'
import { useSpaceStore } from '@/stores/permissions/spaceStore'
import { useProjectPermissionsStore } from '@/stores/permissions/projectStore'

/**
 * Композабл для работы с правами на всех уровнях
 * Единая точка доступа к системе прав
 *
 * Уровни проверки:
 * 1. License (глобальный) - лимиты и фичи тарифа пользователя
 * 2. Space - права на пространства и проекты в них
 * 3. Project - детальные права в текущем проекте
 */
export function usePermissions() {
  const license = useLicenseStore()
  const space = useSpaceStore()
  const project = useProjectPermissionsStore()

  // === Состояние загрузки ===

  const isLoading = computed(() =>
    license.isLoading || space.isLoading || project.isLoading
  )

  const isInitialized = computed(() =>
    license.tariff !== null
  )

  // === Каскадная проверка прав ===

  /**
   * Универсальная функция проверки прав
   * Работает с двумя режимами:
   * 1. С projectId - проверяет любой проект через spaceStore (уровень 2)
   * 2. Без projectId - проверяет текущий проект через projectStore (уровень 3)
   *
   * @param {string} permission - название права
   * @param {object} options - опции проверки
   * @param {number} options.projectId - ID проекта (для проверки через spaceStore)
   * @param {boolean} options.contextual - использовать ли контекстные правила
   * @param {any} options.extraCondition - дополнительное условие (для enum 'limited')
   * @param {string} options.expectedValue - ожидаемое значение
   * @param {string} options.role - требуемая роль (только для уровня 3)
   * @param {string} options.tariff - требуемый тариф (только для уровня 3)
   * @param {number} options.spaceId - ID пространства
   * @returns {boolean}
   */
  function can(permission, options = {}) {
    const {
      projectId,
      contextual = false,
      extraCondition,
      expectedValue,
      role: requiredRole,
      tariff: requiredTariff,
      spaceId
    } = options

    // === РЕЖИМ 1: Проверка конкретного проекта (spaceStore - уровень 2) ===
    if (projectId !== undefined) {
      // Используем spaceStore для проверки любого проекта по ID
      const hasPerm = space.hasProjectPermission(projectId, permission, {
        extraCondition
      })

      if (!hasPerm) {
        return false
      }

      // Проверка фичи: если projectStore загружен для этого проекта - из тарифа проекта
      const feature = extractFeatureFromPermission(permission)
      if (feature && !hasFeature(feature)) {
        return false
      }

      return true
    }

    // === РЕЖИМ 2: Проверка текущего проекта (projectStore - уровень 3) ===

    // 1. Проверка загрузки
    if (!project.isInitialized) {
      return false
    }

    // 2. Проверка роли (если указана)
    if (requiredRole && !project.hasRole(requiredRole)) {
      return false
    }

    // 3. Проверка тарифа проекта (если указан)
    if (requiredTariff && !project.hasTariff(requiredTariff)) {
      return false
    }

    // 4. Проверка владения пространством (owner имеет все права)
    if (spaceId && space.isSpaceOwner(spaceId)) {
      const feature = extractFeatureFromPermission(permission)
      if (feature) {
        return license.hasFeature(feature)
      }
      return true
    }

    // 5. Основная проверка права в текущем проекте
    const hasProjectPerm = project.hasPermission(permission, {
      contextual,
      extraCondition,
      expectedValue
    })

    if (!hasProjectPerm) {
      return false
    }

    // 6. Проверка доступности фичи (из тарифа проекта или лицензии)
    const feature = extractFeatureFromPermission(permission)
    if (feature && !hasFeature(feature)) {
      return false
    }

    return true
  }

  /**
   * Проверка доступности фичи
   *
   * Логика:
   * - Внутри проекта (projectStore загружен): фичи из тарифа ПРОЕКТА
   *   (проект мог быть создан с более продвинутой лицензией)
   * - Вне проекта: фичи из лицензии ПОЛЬЗОВАТЕЛЯ
   *
   * @param {string} featureName - название фичи
   * @param {object} options - опции
   * @param {string} options.source - 'license' | 'project' (принудительный выбор источника)
   */
  function hasFeature(featureName, options = {}) {
    const { source } = options

    // Явное указание источника
    if (source === 'license') {
      return license.hasFeature(featureName)
    }

    if (source === 'project') {
      if (!project.isInitialized) return false
      return resolveFeatureValue(project.getTariffFeature(featureName))
    }

    // Автоматический выбор: если внутри проекта → тариф проекта
    if (project.isInitialized) {
      const projectValue = project.getTariffFeature(featureName)
      if (projectValue !== null && projectValue !== undefined) {
        return resolveFeatureValue(projectValue)
      }
    }

    // Вне проекта → лицензия пользователя
    return license.hasFeature(featureName)
  }

  /**
   * Преобразование значения фичи в boolean
   */
  function resolveFeatureValue(value) {
    if (value === null || value === undefined) return false
    if (typeof value === 'boolean') return value
    if (value === 'true') return true
    if (value === 'full') return true
    if (value === 'limited') return true
    if (value === 'none' || value === 'false') return false
    return Boolean(value)
  }

  /**
   * Проверка роли текущего проекта
   */
  function hasRole(roleName) {
    return project.hasRole(roleName)
  }

  /**
   * Проверка владения пространством
   */
  function isSpaceOwner(spaceId) {
    return space.isSpaceOwner(spaceId)
  }

  /**
   * Проверка лимита
   */
  function checkLimit(limitName, currentValue) {
    const limit = license.limits[limitName]?.value
    if (!limit || limit === -1) return true // -1 = безлимит
    return currentValue < limit
  }

  /**
   * Получить информацию о лимите
   */
  function getLimit(limitName) {
    return license.limits[limitName]
  }

  /**
   * Получить значение права (из projectStore - уровень 3)
   */
  function getPermissionValue(permissionName) {
    return project.getPermission(permissionName)
  }

  // === Проверка прав проекта (универсальная) ===

  /**
   * Проверка права проекта (работает с обоими уровнями)
   * Приоритет: projectStore (уровень 3) -> spaceStore (уровень 2)
   *
   * @param {number} projectId - ID проекта
   * @param {string} permissionName - название права
   * @param {object} options - опции проверки
   * @param {boolean} options.extraCondition - дополнительное условие для 'limited'
   * @param {string} options.source - явное указание источника: 'space' | 'project'
   */
  function checkProjectPermission(projectId, permissionName, options = {}) {
    const { source } = options

    // Если явно указан источник
    if (source === 'space') {
      return space.hasProjectPermission(projectId, permissionName, options)
    }

    if (source === 'project') {
      return project.hasPermission(permissionName, options)
    }

    // Автоматический выбор источника
    // Приоритет: projectStore (если загружен для этого проекта) -> spaceStore
    if (project.isInitialized && project.currentProjectId === projectId) {
      return project.hasPermission(permissionName, options)
    }

    // Иначе используем spaceStore
    return space.hasProjectPermission(projectId, permissionName, options)
  }

  /**
   * Получить raw значение права проекта
   * @param {number} projectId - ID проекта
   * @param {string} permissionName - название права
   * @param {object} options - опции
   * @param {string} options.source - источник: 'space' | 'project'
   */
  function getProjectPermissionValue(projectId, permissionName, options = {}) {
    const { source } = options

    if (source === 'space') {
      return space.getProjectPermissionValue(projectId, permissionName)
    }

    if (source === 'project') {
      return project.getPermission(permissionName)
    }

    // Автоматический выбор: приоритет projectStore
    if (project.isInitialized && project.currentProjectId === projectId) {
      return project.getPermission(permissionName)
    }

    return space.getProjectPermissionValue(projectId, permissionName)
  }

  // === Роли и лицензия ===

  /**
   * Получить роли пользователя
   */
  const roles = computed(() => license.roles)

  /**
   * Получить названия ролей
   */
  const roleTitles = computed(() => license.roleTitles)

  /**
   * Получить пространства пользователя
   */
  const userSpaces = computed(() => license.spaces)

  /**
   * Тег тарифа
   */
  const licenseTag = computed(() => license.tag)

  /**
   * Название тарифа
   */
  const licenseTitle = computed(() => license.title)

  /**
   * Фичи тарифа
   */
  const licenseFeatures = computed(() => license.templates)

  /**
   * Лимиты тарифа
   */
  const licenseLimits = computed(() => license.limits)

  /**
   * Проверка роли пользователя
   */
  function hasUserRole(roleTitle) {
    return license.roleTitles.includes(roleTitle)
  }

  /**
   * Проверка любой из ролей
   */
  function hasAnyRole(roleTitlesArray) {
    if (!Array.isArray(roleTitlesArray)) return false
    return roleTitlesArray.some(role => license.roleTitles.includes(role))
  }

  /**
   * Проверка роли в пространстве
   */
  function hasSpaceRole(spaceId, roleTitle) {
    return userSpaces.value[spaceId] === roleTitle
  }

  /**
   * Проверка тарифа
   */
  function hasLicense(licenseTagValue) {
    return licenseTag.value === licenseTagValue
  }

  /**
   * Проверка любого из тарифов
   */
  function hasAnyLicense(licenseTags) {
    if (!Array.isArray(licenseTags)) return false
    return licenseTags.includes(licenseTag.value)
  }

  /**
   * Получить значение фичи
   * @param {string} featureName - название фичи
   * @param {string} source - 'license' | 'project' (по умолчанию license)
   */
  function getFeatureValue(featureName, source = 'license') {
    if (source === 'project' && project.isInitialized) {
      return project.getTariffFeature(featureName)
    }
    return license.getFeatureValue(featureName)
  }

  /**
   * Максимальный уровень доступа
   */
  const maxAccessLevel = computed(() => {
    const AccessLevels = {
      'Owner': 100,
      'Admin': 80,
      'Manager': 60,
      'Editor': 40,
      'User': 20,
      'Guest': 10
    }

    return Math.max(...roleTitles.value.map(role => AccessLevels[role] || 0))
  })

  /**
   * Проверка уровня доступа
   */
  function hasAccessLevel(minLevel) {
    return maxAccessLevel.value >= minLevel
  }

  // === Инициализация ===

  /**
   * Инициализация системы прав при загрузке приложения
   * Вызывается после успешной авторизации
   * @param {object} authData - { tariff, roles }
   */
  async function initialize(authData) {
    // 1. Загружаем тариф и роли пользователя
    await license.load({
      tariff: authData.tariff,
      roles: authData.roles
    })

    // 2. Загружаем пространства (опционально, можно отложить)
    // await space.load()
  }

  /**
   * Загрузить права для конкретного проекта
   * Вызывается при открытии проекта
   */
  async function loadProject(projectId) {
    await project.loadForProject(projectId)
  }

  /**
   * Очистка при logout
   */
  function cleanup() {
    license.clear()
    space.clear()
    project.clear()
  }

  return {
    // Stores (для прямого доступа если нужно)
    license,
    space,
    project,

    // Состояние
    isLoading,
    isInitialized,

    // Роли и лицензия
    roles,
    roleTitles,
    userSpaces,
    licenseTag,
    licenseTitle,
    licenseFeatures,
    licenseLimits,
    maxAccessLevel,

    // Основные методы
    can,
    hasFeature,
    hasRole,
    hasUserRole,
    hasAnyRole,
    hasSpaceRole,
    hasLicense,
    hasAnyLicense,
    hasAccessLevel,
    isSpaceOwner,
    checkLimit,
    getLimit,
    getPermissionValue,
    getFeatureValue,

    // Проверка прав проекта
    checkProjectPermission,
    getProjectPermissionValue,

    // Инициализация
    initialize,
    loadProject,
    cleanup
  }
}

// === Helpers ===

/**
 * Извлекает название фичи из названия права
 * Например: can_create_comments -> comments
 */
function extractFeatureFromPermission(permission) {
  const featureMap = {
    'comment': 'comments',
    'subtask': 'subtasks',
    'checklist': 'checklists',
    'controller': 'controllers',
    'tag': 'tags',
    'calendar': 'calendar',
    'dashboard': 'dashboard_view',
    'gantt': 'gantt_chart',
    'kanban': 'kanban',
    'archive': 'archive'
  }

  for (const [key, feature] of Object.entries(featureMap)) {
    if (permission.toLowerCase().includes(key)) {
      return feature
    }
  }

  return null
}
